import React, { useRef, useState, useEffect } from "react";

import event_card_go_left from "../../../assets/images/event_card_go_left.svg";
import event_card_go_right from "../../../assets/images/event_card_go_right.svg";
import event_card_folderTab from "../../../assets/images/event_card_folderTab.svg";
import event_card_folderTab_active from "../../../assets/images/event_card_folderTab_active.svg";
import "./EventsSection.css";

const EventsSection = ({
  events,
  onEventClick,
  eventLoader = {},
  activeEvent = null,
}) => {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const resizeObserverRef = useRef(null);

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;

      const isAtStart = scrollLeft <= 1;
      const isAtEnd = Math.abs(scrollWidth - (scrollLeft + clientWidth)) <= 1;

      setCanScrollLeft(!isAtStart);
      setCanScrollRight(!isAtEnd && scrollWidth > clientWidth);
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      resizeObserverRef.current = new ResizeObserver(() => {
        requestAnimationFrame(checkScroll);
      });

      resizeObserverRef.current.observe(scrollContainer);
      if (scrollContainer.parentElement) {
        resizeObserverRef.current.observe(scrollContainer.parentElement);
      }

      const handleScroll = () => {
        requestAnimationFrame(checkScroll);
      };

      scrollContainer.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", handleScroll);

      checkScroll();

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleScroll);
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
      };
    }
  }, [events]);

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = 180;
      const gap = 16;
      const scrollAmount = cardWidth + gap;

      const newScrollPosition =
        container.scrollLeft +
        (direction === "left" ? -scrollAmount : scrollAmount);

      container.scrollTo({
        left: newScrollPosition,
        behavior: "smooth",
      });

      setTimeout(() => {
        requestAnimationFrame(checkScroll);
      }, 500);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const parseEventString = (eventString) => {
    const roundSplit = eventString.split("-Round");
    const roundNumber = roundSplit.length > 1 ? `Round${roundSplit[1]}` : "";
    const beforeRound = roundSplit[0];

    const roundKeywords = ["Semi", "Prelims", "Finals"];
    let roundStartIndex = -1;
    let roundKeyword = "";

    for (const keyword of roundKeywords) {
      const index = beforeRound.indexOf(`-${keyword}`);
      if (index !== -1) {
        roundStartIndex = index;
        roundKeyword = keyword;
        break;
      }
    }

    if (roundStartIndex === -1) {
      return { name: beforeRound, round: "", roundNumber };
    }

    const name = beforeRound.substring(0, roundStartIndex);
    const round = beforeRound.substring(roundStartIndex + 1);

    return { name, round, roundNumber };
  };

  const sortedEvents = events
    ? [...events].sort((a, b) => {
        return new Date(b.start_date_time) - new Date(a.start_date_time);
      })
    : [];

  return (
    <div className="events-section">
      {sortedEvents.length > 0 && (
        <div className="events-container">
          <div className="events-scroll-container" ref={scrollContainerRef}>
            {sortedEvents.map((event, index) => {
              const { name, round, roundNumber } = parseEventString(
                event.event
              );
              const isLoading = eventLoader[event.event];
              const isActive =
                activeEvent &&
                activeEvent.event === event.event &&
                activeEvent.start_date_time === event.start_date_time;
              return (
                <div
                  key={`${event.event}-${event.start_date_time}`}
                  className={`event-card ${isActive ? "active" : ""} ${
                    isLoading ? "loading" : ""
                  }`}
                  onClick={() => onEventClick && onEventClick(event)}
                  style={{
                    cursor: onEventClick ? "pointer" : "default",
                    position: "relative",
                  }}
                >
                  <div className="event-folder-tab-container">
                    <img
                      src={
                        isActive
                          ? event_card_folderTab_active
                          : event_card_folderTab
                      }
                      alt="folder tab"
                      className="event-folder-tab"
                    />
                    <span className="folder-tab-text">
                      {event.event_type.charAt(0).toUpperCase() +
                        event.event_type.slice(1).toLowerCase()}
                    </span>
                  </div>
                  <div className="event-name">{name}</div>
                  <div className="event-round">{`${round}-${roundNumber}`}</div>
                  <div className="event-date">
                    {formatDate(event.start_date_time)}
                  </div>
                  {isLoading && <div className="event-loader">Loading...</div>}
                </div>
              );
            })}
          </div>

          <button
            className="scroll-button left"
            onClick={() => handleScroll("left")}
            disabled={!canScrollLeft}
            aria-label="Scroll left"
          >
            <img src={event_card_go_left} alt="Scroll left" />
          </button>
          <button
            className="scroll-button right"
            onClick={() => handleScroll("right")}
            disabled={!canScrollRight}
            aria-label="Scroll right"
          >
            <img src={event_card_go_right} alt="Scroll right" />
          </button>
        </div>
      )}
    </div>
  );
};

export default EventsSection;
